import { createContext, useContext, type ReactNode } from 'react';
import { useDescope } from '@/hooks/useDescope';
import type { AuthState } from '@/types/auth';

interface AuthContextValue extends AuthState {
  signInWithMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Props for AuthProvider component
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication provider component that wraps the application
 * and provides authentication state and methods
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const auth = useDescope();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access authentication context
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
